
import React, { useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import LoginForm from '@/components/features/auth/LoginForm';
import LoadingScreen from '@/components/common/LoadingScreen';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Lock, ShieldX } from 'lucide-react';

const Index = () => {
  const { user, token, hasuraToken, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if user was redirected from a protected route
  const fromPath = location.state?.from?.pathname;
  const requiresAuth = location.state?.requiresAuth;
  const accessDenied = location.state?.accessDenied;
  const userType = location.state?.userType;
  const requiredTypes = location.state?.requiredTypes;
  const redirectFromPath = location.state?.fromPath;

  // Set browser tab title for login page
  useEffect(() => {
    document.title = "HBOX";
  }, []);

  // Move useEffect to the top to ensure hooks are called in consistent order
  useEffect(() => {
    if (user && token && hasuraToken) {
      console.log('Index: User authenticated, redirecting based on role:', user.type);
      
      // If user came from a specific route, try to redirect them back there
      if (fromPath && fromPath !== '/') {
        // Check if user has access to that route based on URL patterns
        const hasAccess = checkRouteAccess(fromPath, user.type);
        if (hasAccess) {
          navigate(fromPath, { replace: true });
          return;
        }
      }
      
      // Default role-based redirection - all roles use /dashboard
      switch (user.type) {
        case 'EA': // VPE Admin role
        case 'ES': // Enrollment Specialist
        case 'CA': // Care Specialist Admin role
        case 'CS': // Care Specialist
        case 'PA': // Patient Enrollment Specialist Admin role
        case 'PE': // Patient Enrollment Specialist
          navigate('/dashboard');
          break;
        case 'ED': // Patient Enrollment Specialist
          navigate('/admin');
          break;
        default:
          console.log('Index: Unknown user role:', user.type);
          break;
      }
    }
  }, [user, token, hasuraToken, navigate, fromPath]);

  // Helper function to check if user has access to a route
  const checkRouteAccess = (path: string, userType: string): boolean => {
    const routePermissions: Record<string, string[]> = {
      '/dashboard': ['EA', 'ES', 'CA', 'CS', 'PA', 'PE'],
      '/global-search': ['EA', 'ES', 'CA', 'CS', 'PA', 'PE'] // All authenticated users
    };

    const allowedTypes = routePermissions[path];
    return allowedTypes ? allowedTypes.includes(userType) : false;
  };

  // Helper function to get role-appropriate dashboard for a user
  const getUserDashboard = (userType: string): string => {
    switch (userType) {
      case 'EA':
      case 'ES':
      case 'CA':
      case 'CS':
      case 'PA':
      case 'PE':
        return '/dashboard';
      case 'ED':
        return '/admin';
      default:
        return '/';
    }
  };

  // Show loading screen while initializing
  if (isLoading) {
    return <LoadingScreen />;
  }

  // Show login form if not authenticated
  if (!user || !token || !hasuraToken) {
    return (
      <div className="min-h-screen bg-gray-50">
        <LoginForm />
      </div>
    );
  }

  // Show access denied message for authenticated users without proper permissions
  if (accessDenied) {
    const userDashboard = getUserDashboard(userType);
    
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Access Denied Alert */}
        <div className="bg-red-50 border-b border-red-200 px-4 py-3">
          <Alert className="max-w-4xl mx-auto border-red-200 bg-red-50">
            <ShieldX className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800 font-medium">
              Access denied to {redirectFromPath}. Your role "{userType}" does not have permission to access this page. 
              {requiredTypes && ` Required roles: ${requiredTypes.join(', ')}`}
            </AlertDescription>
          </Alert>
        </div>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center" id="access-denied">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
            <p className="text-gray-600 mb-6">
              You don't have permission to access {redirectFromPath}.
            </p>
            <button
              onClick={() => navigate(userDashboard, { replace: true })}
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-medium"
            >
              Go to My Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Only show default view for authenticated users with unknown roles
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center" id="default-dashboard">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-4" id="welcome-message">
          Welcome, {user.fullName}
        </h1>
        <p className="text-gray-600" id="role-message">
          Dashboard for role "{user.type}" is being developed.
        </p>
      </div>
    </div>
  );
};

export default Index;
